import store from 'app/store';
import { ConfigurationType, SystemOptionType } from 'data/types';
import {
  CONFIRMATION_RULE_IDS,
  CONFIRMATION_RULE_TEXT
} from 'data/validation-rule-config';
import { getItem } from 'utils/data';
import { findSpaceById } from 'utils/structure';

export type ConfirmationRuleFunction = (
  config: ConfigurationType,
  systemOption: SystemOptionType
) => boolean;

const stubConfirmation: ConfirmationRuleFunction = () => false;

const BEDROOM_KEYWORDS = ['bedroom', 'bed room', 'master bed', 'guest bed'];
const SMALL_SPACE_KEYWORDS = ['closet', 'pantry', 'laundry', 'utility'];
const KITCHEN_KEYWORDS = ['kitchen'];

function labelMatchesAny(label: string, keywords: string[]): boolean {
  return keywords.some((kw) => label.includes(kw));
}

function getZoneItemLabels(zone: {
  zoneItems: { structureItemId: string }[];
}): string[] {
  const { structure } = store.getState().structure;
  return zone.zoneItems
    .map((zi) => {
      const space = findSpaceById(structure, zi.structureItemId);
      if (!space) return null;
      const item = getItem(space.itemId);
      return item?.label?.toLowerCase() ?? null;
    })
    .filter(Boolean) as string[];
}

export const confirmationRuleFunctions: Record<
  string,
  ConfirmationRuleFunction
> = {
  zoneDistanceImbalanceNote: (config) =>
    config.condensers.some((condenser) => {
      if (condenser.position === undefined) return false;
      const zoneDistances = condenser.zones
        .filter((z) => z.distanceFromCondenser !== undefined)
        .map((z) => z.distanceFromCondenser as number);
      return (
        zoneDistances.length > 1 &&
        Math.max(...zoneDistances) > Math.min(...zoneDistances) * 2
      );
    }),

  heatingCapacityImbalanceNote: (config) =>
    config.condensers.some((condenser) => {
      if (condenser.position === undefined) return false;
      const zoneHeatingBTUs = condenser.zones.map((zone) =>
        zone.zoneItems.reduce((acc, item) => acc + item.BTUs.heating, 0)
      );
      const minHeatingBTU = Math.min(...zoneHeatingBTUs);
      const maxHeatingBTU = Math.max(...zoneHeatingBTUs);
      return maxHeatingBTU > minHeatingBTU * 3;
    }),

  differentSunExposure: stubConfirmation,

  openConceptFightingThermostats: (config) => {
    const { structure } = store.getState().structure;
    return config.condensers.some((condenser) => {
      const allZoneSectionIds = condenser.zones.map((zone) => {
        const sectionIds = new Set(
          zone.zoneItems.map((zi) => {
            for (const level of structure) {
              for (const section of level.sections) {
                if (
                  section.spaces.some((s) => s.uniqueId === zi.structureItemId)
                ) {
                  return section.uniqueId;
                }
              }
            }
            return null;
          })
        );
        return sectionIds;
      });
      for (let i = 0; i < allZoneSectionIds.length; i += 1) {
        for (let j = i + 1; j < allZoneSectionIds.length; j += 1) {
          for (const sectionId of allZoneSectionIds[i]) {
            if (sectionId && allZoneSectionIds[j].has(sectionId)) {
              return true;
            }
          }
        }
      }
      return false;
    });
  },

  smallSpaceDedicatedZone: (config) =>
    config.condensers.some((condenser) =>
      condenser.zones.some((zone) => {
        if (zone.zoneItems.length !== 1) return false;
        const labels = getZoneItemLabels(zone);
        return labels.some((l) => labelMatchesAny(l, SMALL_SPACE_KEYWORDS));
      })
    ),

  kitchenMakeupAir: (config) =>
    config.condensers.some((condenser) =>
      condenser.zones.some((zone) => {
        const labels = getZoneItemLabels(zone);
        return labels.some((l) => labelMatchesAny(l, KITCHEN_KEYWORDS));
      })
    ),

  singleCondenserNoRedundancy: (config) => {
    const visible = config.condensers.filter((c) => c.position !== undefined);
    return visible.length === 1;
  },

  prefer1to1FarZones: (config) =>
    config.condensers.some((condenser) => {
      if (condenser.position === undefined) return false;
      if (condenser.zones.length < 2) return false;
      const distances = condenser.zones
        .filter((z) => z.distanceFromCondenser !== undefined)
        .map((z) => z.distanceFromCondenser as number);
      if (distances.length < 2) return false;
      return Math.max(...distances) > Math.min(...distances) * 3;
    }),

  complexityWarning: (config) => {
    const visible = config.condensers.filter((c) => c.position !== undefined);
    const totalZones = visible.reduce((acc, c) => acc + c.zones.length, 0);
    return visible.length >= 3 || totalZones >= 6;
  },

  pumpNoiseRiskBedrooms: (config) =>
    config.condensers.some((condenser) =>
      condenser.zones.some((zone) => {
        if (!zone.airHandler) return false;
        const labels = getZoneItemLabels(zone);
        return labels.some((l) => labelMatchesAny(l, BEDROOM_KEYWORDS));
      })
    ),

  wallHeadOverHeatSource: stubConfirmation,
  wallHeadDischargeBlocked: stubConfirmation,
  shortestPracticalLineset: stubConfirmation,
  additionalRefrigerantChargeRequired: stubConfirmation,
  trapLoopNeededVerticalDrops: stubConfirmation,
  leaveExtraWiringLength: stubConfirmation,
  outdoorDisconnectRequired: stubConfirmation,
  addRiseAfterPumpDischarge: stubConfirmation,
  outdoorUnitNoiseBedroom: stubConfirmation,
  coastalCorrosiveProtection: stubConfirmation,
  outdoorUnitDefrostWater: stubConfirmation,
  differentControlStylesInHome: stubConfirmation,
  backupHeatExtremeCold: stubConfirmation,
  highAltitudeDerating: stubConfirmation,
  ventilationNeededTightHomes: stubConfirmation,
  filterAccessBlocked: stubConfirmation
};

export function getConfirmationMessagesForSystem(
  config: ConfigurationType,
  systemOption: SystemOptionType
): string[] {
  const messages: string[] = [];
  CONFIRMATION_RULE_IDS.forEach((ruleId) => {
    const fn = confirmationRuleFunctions[ruleId];
    if (fn?.(config, systemOption)) {
      const text = CONFIRMATION_RULE_TEXT[ruleId];
      if (text) messages.push(text);
    }
  });
  return messages;
}
